import {
  Autosuggest,
  Box,
  Button,
  ColumnLayout,
  DatePicker,
  ExpandableSection,
  Input,
  Modal,
  Multiselect,
  Pagination,
  Select,
  SelectProps,
  SpaceBetween,
  Table,
  Textarea,
} from '@cloudscape-design/components';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CollectRequest,
  Place,
  PlaceList,
  PlaceSearchItem,
  PlaceSearchItemWithName,
  SearchItem,
  SearchRequest,
} from '../common/types';
import { collectPlaces, searchPlaces } from '../common/api/places';
import { useMemo, useState } from 'react';
import {
  Container,
  Header,
  AppLayout,
  ContentLayout,
  Grid,
  Toggle,
  Form,
  Alert,
  FormField,
  StatusIndicator,
} from '@cloudscape-design/components';
import { ErrorMessage } from './ErrorMessage';
import { CreateListModal } from './CreateListModal';
import { getAllLists } from '../common/api/lists';
import { useCollection } from '@cloudscape-design/collection-hooks';

export type SearchPlacesModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onComplete?: () => void;
};

export function SearchPlacesModal(props: SearchPlacesModalProps) {
  const { open, setOpen, onComplete } = props;

  const [names, setNames] = useState<string>('');

  const [includeServices, setIncludeServices] = useState<boolean>(false);
  const [includeParts, setIncludeParts] = useState<boolean>(false);

  const [searched, setSearched] = useState<boolean>(false);

  const [allItems, setAllItems] = useState<PlaceSearchItemWithName[]>([]);
  const [selectedItems, setSelectedItems] = useState<PlaceSearchItemWithName[]>([]);

  const [createListOpen, setCreateListOpen] = useState<boolean>(false);
  const [selectedLists, setSelectedLists] = useState<PlaceList[]>([]);

  const [now, setNow] = useState<Date>(new Date());

  const listQuery = useQuery({
    queryKey: ['lists', 'all', now],
    queryFn: getAllLists,
    enabled: open,
  });

  const listLookup = useMemo(() => {
    return listQuery.data?.reduce(
      (acc, list) => {
        acc[list.uuid] = list;
        return acc;
      },
      {} as Record<string, PlaceList>,
    );
  }, [listQuery.data]);

  const options = useMemo(() => {
    return listQuery.data?.map((list) => {
      return {
        label: list.name,
        value: list.uuid,
        description: list.description ?? undefined,
      };
    });
  }, [listQuery.data]);

  const selectedOptions = useMemo(() => {
    return selectedLists.map((list) => {
      return {
        label: list.name,
        value: list.uuid,
        description: list.description ?? undefined,
      };
    });
  }, [selectedLists]);

  const close = () => {
    setOpen(false);
    setNames('');
    setIncludeServices(false);
    setIncludeParts(false);
    setAllItems([]);
    setSelectedItems([]);
    setSearched(false);
    setSelectedLists([]);
  };

  const search = useMutation({
    mutationFn: async (data: SearchRequest) => {
      const result = await searchPlaces(data);
      if (!result) throw new Error('Search returned no data');
      return result;
    },
    onSuccess: (data: SearchItem[]) => {
      setSearched(true);
      const seenItems: Record<string, boolean> = {};
      const newItems: PlaceSearchItemWithName[] = [];
      const newFirstItems: PlaceSearchItemWithName[] = [];
      for (const item of data) {
        if (item.places.length === 0) {
          newItems.push({
            name: 'No results found',
            place_id: '-',
            formatted_address: '-',
            search_name: item.name,
          });
        } else {
          const firstPlace = item.places[0];
          if (seenItems[firstPlace.place_id]) {
            const newItem = {
              name: 'Previously found',
              place_id: '-',
              formatted_address: '-',
              search_name: item.name,
            };
            newItems.push(newItem);
            continue;
          }
          seenItems[firstPlace.place_id] = true;
          const newItem = { ...firstPlace, search_name: item.name };
          newItems.push(newItem);
          newFirstItems.push(newItem);
        }
      }
      for (const item of data) {
        if (item.places.length >= 2) {
          for (const place of item.places) {
            if (seenItems[place.place_id]) {
              continue;
            }
            seenItems[place.place_id] = true;
            newItems.push({ ...place, search_name: item.name });
          }
        }
      }
      setAllItems(newItems);
      setSelectedItems(newFirstItems);
    },
  });

  const collect = useMutation({
    mutationFn: collectPlaces,
    onSuccess: () => {
      if (onComplete) {
        onComplete();
      }
      search.reset();
      collect.reset();
      close();
    },
  });

  const handleCreate = () => {
    if (search.isPending) {
      return;
    }
    if (!names.trim()) {
      return;
    }

    const data: SearchRequest = {
      names: names.trim(),
      include_services: includeServices,
      include_parts: includeParts,
    };
    search.mutate(data);
  };

  const handleCollect = () => {
    if (collect.isPending) {
      return;
    }
    if (selectedItems.length === 0) {
      return;
    }
    const data: CollectRequest = {
      place_ids: selectedItems.map((item) => item.place_id).filter((id) => id !== '-'),
      list_names: selectedLists ? selectedLists.map((list) => list.name) : undefined,
      // TODO give option to skip
      crawl: true,
      reviews: true,
      scorecards: true,
    };
    collect.mutate(data);
  };

  const { items, paginationProps } = useCollection(allItems, {
    pagination: { pageSize: 10 },
  });

  return (
    <Modal
      size="max"
      onDismiss={close}
      visible={open}
      closeAriaLabel="Close"
      header="Add new Places"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close}>
              Cancel
            </Button>
            <Button variant="link" onClick={() => setSearched(false)}>
              Back
            </Button>
            <Button
              loading={searched ? collect.isPending : search.isPending}
              variant="primary"
              onClick={searched ? handleCollect : handleCreate}
              disabled={!names.trim() || (searched && selectedItems.length === 0)}
            >
              {searched ? 'Add Places' : 'Search'}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      {!searched ? (
        <SpaceBetween size="m">
          <FormField
            description="The names of the Google review places."
            constraintText="Enter one name per line corresponding to each place."
            label="Place Names"
          >
            <Textarea
              autoFocus
              onChange={({ detail }) => setNames(detail.value)}
              value={names}
              placeholder="One name per line..."
              rows={10}
            />
          </FormField>
          <FormField
            description="Include the service department as a separate place when collecting contacts."
            label="Include Service Department"
          >
            <Toggle
              onChange={({ detail }) => setIncludeServices(detail.checked)}
              checked={includeServices}
            />
          </FormField>
          <FormField
            description="Include the parts department as a separate place when collecting contacts."
            label="Include Parts Department"
          >
            <Toggle
              onChange={({ detail }) => setIncludeParts(detail.checked)}
              checked={includeParts}
            />
          </FormField>
          {search.error && <ErrorMessage loading={search.isPending} error={search.error} />}
        </SpaceBetween>
      ) : (
        <SpaceBetween size="m">
          <SpaceBetween size="m">
            <Table
              items={items}
              pagination={<Pagination {...paginationProps} />}
              selectedItems={selectedItems}
              onSelectionChange={
                setSelectedItems
                  ? ({ detail }) => setSelectedItems(detail.selectedItems)
                  : undefined
              }
              isItemDisabled={(item) => item.formatted_address === '-' || collect.isPending}
              selectionType="multi"
              variant="embedded"
              columnDefinitions={[
                {
                  id: 'search_name',
                  header: 'Searched Name',
                  cell: (item) => <Box variant="p">{item.search_name}</Box>,
                },
                {
                  id: 'name',
                  header: 'Found Name',
                  cell: (item) => <Box variant="p">{item.name}</Box>,
                },
                {
                  id: 'formatted_address',
                  header: 'Address',
                  cell: (item) => <Box variant="p">{item.formatted_address}</Box>,
                },
              ]}
              // TODO consider pagination here
            />
          </SpaceBetween>
          <SpaceBetween size="m">
            <FormField
              description="Optionally add the places to one or more lists."
              label="Add to Lists"
              secondaryControl={
                <Button
                  variant="link"
                  onClick={() => setCreateListOpen(true)}
                  iconName="add-plus"
                  disabled={collect.isPending}
                >
                  Create
                </Button>
              }
            >
              <Multiselect
                selectedOptions={selectedOptions}
                onChange={({ detail }) =>
                  setSelectedLists(
                    listLookup
                      ? detail.selectedOptions.map((option) => listLookup[option.value as string])
                      : [],
                  )
                }
                options={options || []}
                loadingText="Loading lists..."
                placeholder="Select lists..."
                statusType={
                  listQuery.isLoading ? 'loading' : listQuery.isError ? 'error' : 'finished'
                }
                empty={
                  <Box textAlign="center" color="inherit">
                    No lists found
                  </Box>
                }
                disabled={collect.isPending}
              />
              <CreateListModal
                open={createListOpen}
                setOpen={setCreateListOpen}
                onComplete={(placeList) => {
                  listQuery.refetch();
                  setSelectedLists([...selectedLists, placeList]);
                }}
              />
            </FormField>
          </SpaceBetween>
          {collect.error && <ErrorMessage loading={collect.isPending} error={collect.error} />}
        </SpaceBetween>
      )}
    </Modal>
  );
}
